/**
 * SummaryContent — orchestrateur : mappe un `SummaryDetail` vers les 6 zones
 * canoniques (Identité → Badges → Alerte → KPIs → Sections → Actions).
 *
 * Conteneur `flex flex-col gap-5 p-5 text-sm` — identique à `SearchPreviewPanel`.
 * Un seul `border-t` dans tout le volet (porté par le BlocActions). Toute zone
 * vide est omise en amont (backend) ou masquée par son bloc.
 */

import { SummaryActionsBlock } from './blocks/SummaryActionsBlock'
import { SummaryAlertBlock } from './blocks/SummaryAlertBlock'
import { SummaryBadges } from './blocks/SummaryBadges'
import { ClientHealthCard } from './blocks/ClientHealthCard'
import { SummaryDocumentBlock } from './blocks/SummaryDocumentBlock'
import { SummaryHeaderBlock } from './blocks/SummaryHeaderBlock'
import { SummaryKpis } from './blocks/SummaryKpis'
import { SummarySectionBlock } from './blocks/SummarySectionBlock'
import type { SummaryDetail } from './types'

interface SummaryContentProps {
    data: SummaryDetail
    currentEntityId: string | null
    onOpen: () => void
    onNavigate: (href: string) => void
    onSendEmail: (target: { email: string; name: string }) => void
    /** Lecteur plein écran d'une pièce (⌘K et volet). Absent → action omise. */
    onQuickLook?: (fileId: string) => void
    /**
     * Bloc « Santé entreprise » d'un client (zone [5bis]).
     *
     * ⚠️ FAUX par défaut dans la PALETTE. Ce bloc déclenche un fetch de détail
     * client **et** tire le bundle santé financière : flécher sur cinq clients
     * en déclencherait cinq. L'aperçu ⌘K est secondaire au listing et ne doit
     * pas dégrader son SLO (SEARCH-001 §6.10) — alors qu'épingler une fiche
     * dans le volet est un geste délibéré, qui justifie la dépense.
     */
    showClientHealth?: boolean
}

export function SummaryContent({
    data,
    currentEntityId,
    onOpen,
    onNavigate,
    onSendEmail,
    onQuickLook,
    showClientHealth = true,
}: SummaryContentProps) {
    return (
        <div className="flex flex-col gap-5 p-5 text-sm">
            {/* [1] Identité */}
            <SummaryHeaderBlock header={data.header} />

            {/* [2] Badges */}
            <SummaryBadges
                badges={data.badges}
                entity={data.entity}
                currentEntityId={currentEntityId}
            />

            {/* [3] Alerte */}
            {data.alert && <SummaryAlertBlock alert={data.alert} />}

            {/* [4] KPIs */}
            <SummaryKpis kpis={data.kpis} />

            {/* [4bis] Vignette de la pièce (document du coffre, PDF courant d'un devis…) */}
            {data.document && (
                <SummaryDocumentBlock document={data.document} label={data.header.label} />
            )}

            {/* [5] Sections */}
            {data.sections.map((section) => (
                <SummarySectionBlock
                    key={section.key}
                    section={section}
                    onSendEmail={onSendEmail}
                    onNavigate={onNavigate}
                />
            ))}

            {/* [5bis] CLIENT — bloc dédié « Santé entreprise » (financière, externe),
                réutilise EXACTEMENT le composant de la fiche (jauge + verdict + KPIs). */}
            {data.type === 'client' && showClientHealth && (
                <ClientHealthCard
                    clientId={data.id}
                    entityId={data.entity?.id ?? currentEntityId}
                />
            )}

            {/* [6] Actions */}
            <SummaryActionsBlock
                actions={data.actions}
                onOpen={onOpen}
                onNavigate={onNavigate}
                onSendEmail={onSendEmail}
                onQuickLook={onQuickLook}
            />
        </div>
    )
}
